/**
 * メッセージダイアログヘルパー
 * alert() の代わりに Message-Dialog.vue を使用するためのユーティリティ
 */

import store from '@/store'

/**
 * グローバルメッセージダイアログを表示
 * @param {string} message - 表示するメッセージ（HTMLも可）
 * @param {Object} options - オプション設定
 * @param {string} options.title - タイトル（デフォルト: 'メッセージ'）
 * @param {number} options.maxWidth - 最大幅（デフォルト: 500）
 * @param {boolean} options.persistent - 外側クリックで閉じない（デフォルト: false）
 * @param {string} options.closeText - 閉じるボタンのテキスト（デフォルト: '閉じる'）
 * @param {string} options.closeColor - 閉じるボタンの色（デフォルト: 'primary'）
 * @param {boolean} options.showCloseIcon - Xアイコン表示（デフォルト: true）
 *
 * @example
 * import { showMessage } from '@/js/utils/messageHelper'
 *
 * // 基本的な使い方（alert()の代わり）
 * showMessage('ログインが必要です')
 *
 * // タイトル付き
 * showMessage('保存に成功しました', { title: '成功' })
 *
 * // HTML形式
 * showMessage('<p><strong>注意:</strong> この操作は取り消せません</p>', {
 *   title: '警告',
 *   closeColor: 'error'
 * })
 */
export function showMessage(message, options = {}) {
  const {
    title = 'メッセージ',
    maxWidth = 500,
    persistent = false,
    closeText = '閉じる',
    closeColor = 'primary',
    showCloseIcon = true,
  } = options

  // HTMLエスケープ（<や>が含まれていない場合のみ自動でエスケープ）
  let contentHtml = message
  if (message && !message.includes('<') && !message.includes('>')) {
    // プレーンテキストの場合は改行を<br>に変換
    contentHtml = message.replace(/\n/g, '<br>')
  }

  store.dispatch('messageDialog/open', {
    id: 'global',
    title,
    contentHtml,
    options: {
      maxWidth,
      persistent,
      closeText,
      closeColor,
      showCloseIcon,
    },
  })
}

/**
 * エラーメッセージを表示（赤いボタン）
 * @param {string} message - エラーメッセージ
 * @param {Object} options - オプション設定
 */
export function showError(message, options = {}) {
  showMessage(message, {
    title: 'エラー',
    closeColor: 'error',
    ...options,
  })
}

/**
 * 成功メッセージを表示（緑のボタン）
 * @param {string} message - 成功メッセージ
 * @param {Object} options - オプション設定
 */
export function showSuccess(message, options = {}) {
  showMessage(message, {
    title: '成功',
    closeColor: 'success',
    ...options,
  })
}

/**
 * 警告メッセージを表示（オレンジのボタン）
 * @param {string} message - 警告メッセージ
 * @param {Object} options - オプション設定
 */
export function showWarning(message, options = {}) {
  showMessage(message, {
    title: '警告',
    closeColor: 'warning',
    ...options,
  })
}

/**
 * 情報メッセージを表示（青いボタン）
 * @param {string} message - 情報メッセージ
 * @param {Object} options - オプション設定
 */
export function showInfo(message, options = {}) {
  showMessage(message, {
    title: '情報',
    closeColor: 'info',
    ...options,
  })
}

/**
 * ログインを促すダイアログを表示
 * 「ログイン」ボタンを押すとログインダイアログが開く
 * @param {Object} options - オプション設定
 * @param {string} options.title - タイトル（デフォルト: 'ログインが必要です'）
 * @param {string} options.message - メッセージ（デフォルト: 'この機能はログインユーザー専用です。ご利用いただくには、ログインしてください。'）
 * @param {string} options.icon - アイコン（デフォルト: 'mdi-login'）
 * @param {string} options.iconColor - アイコンの色（デフォルト: 'warning'）
 * @param {number} options.maxWidth - 最大幅（デフォルト: 420）
 *
 * @example
 * import { showLoginPrompt } from '@/js/utils/messageHelper'
 *
 * // 基本的な使い方
 * showLoginPrompt()
 *
 * // カスタムメッセージ
 * showLoginPrompt({
 *   title: 'マイドロー',
 *   message: 'マイドローはログインユーザー専用の機能です。'
 * })
 */
export function showLoginPrompt(options = {}) {
  const {
    title = 'ログインが必要です',
    message = 'この機能はログインユーザー専用です。<br>ご利用いただくには、ログインしてください。',
    icon = 'mdi-login',
    iconColor = 'warning',
    maxWidth = 420,
  } = options

  store.dispatch('messageDialog/open', {
    id: 'login-prompt',
    title,
    contentHtml: `<p>${message}</p>`,
    options: {
      icon,
      iconColor,
      maxWidth,
      showCloseIcon: true,
      // ログインボタン用の特別なオプション
      showLoginButton: true,
    },
  })
}

/**
 * ログインチェックを行い、未ログインならプロンプトを表示
 * @param {Object} options - showLoginPrompt と同じオプション
 * @returns {boolean} ログイン済みなら true、未ログインなら false
 *
 * @example
 * import { requireLogin } from '@/js/utils/messageHelper'
 *
 * onSomeFeature() {
 *   if (!requireLogin({ title: 'マイドロー', icon: 'mdi-cloud' })) return
 *   // ログイン済みの処理
 * }
 */
export function requireLogin(options = {}) {
  const userId = store.state.userId
  if (userId) return true

  showLoginPrompt(options)
  return false
}

/**
 * 今後ログイン専用になる旨の警告を表示（非ブロッキング）
 * 未ログインユーザーにのみ毎回表示し、処理は止めない。
 * @param {string} featureName - 機能名（ダイアログタイトルに使用）
 */
export function warnFutureLogin(featureName = '') {
  const userId = store.state.userId
  if (userId) return

  const title = featureName || 'この機能について'
  store.dispatch('messageDialog/open', {
    id: 'warn-future-login',
    title,
    contentHtml: '<p>この機能は今後、ログインユーザー専用になる予定です。<br>引き続きご利用いただくために、無料アカウントの作成をお願いします。</p>',
    options: {
      icon: 'mdi-information-outline',
      iconColor: 'info',
      maxWidth: 420,
      showCloseIcon: true,
      showLoginButton: true,
    },
  })
}

/**
 * Vue コンポーネント内で使用するためのミックスイン
 *
 * @example
 * import { messageDialogMixin } from '@/js/utils/messageHelper'
 *
 * export default {
 *   mixins: [messageDialogMixin],
 *   methods: {
 *     handleClick() {
 *       this.$showMessage('クリックされました')
 *       this.$showError('エラーが発生しました')
 *       this.$showSuccess('保存しました')
 *     }
 *   }
 * }
 */
export const messageDialogMixin = {
  methods: {
    $showMessage(message, options) {
      showMessage(message, options)
    },
    $showError(message, options) {
      showError(message, options)
    },
    $showSuccess(message, options) {
      showSuccess(message, options)
    },
    $showWarning(message, options) {
      showWarning(message, options)
    },
    $showInfo(message, options) {
      showInfo(message, options)
    },
    $showLoginPrompt(options) {
      showLoginPrompt(options)
    },
  },
}

/**
 * 保護キーワードリスト
 * これらのキーワードが名前に含まれている場合、削除不可
 */
const PROTECTION_KEYWORDS = ['保護', 'ロック', 'プロテクト']

/**
 * アイテムが保護されているかチェック
 * 名前に保護キーワード（「保護」「ロック」「プロテクト」）が含まれている場合は削除不可
 *
 * @param {Object} item - チェック対象のアイテム
 * @param {string} item.name - アイテム名
 * @param {string} item.memo - メモ（photomemo用）
 * @param {string} item.original_filename - 元のファイル名（photomemo用）
 * @returns {boolean} 保護されている場合は true
 *
 * @example
 * import { isItemProtected, showProtectedMessage } from '@/js/utils/messageHelper'
 *
 * if (isItemProtected(item)) {
 *   showProtectedMessage()
 *   return
 * }
 */
export function isItemProtected(item) {
  if (!item) return false
  // name, memo, original_filename, geojson_name, tags のいずれかに保護キーワードが含まれているかチェック
  const fieldsToCheck = [item.name, item.memo, item.original_filename, item.geojson_name, item.tags].filter(Boolean)
  if (fieldsToCheck.length === 0) return false
  return fieldsToCheck.some(field =>
    PROTECTION_KEYWORDS.some(keyword => field.includes(keyword))
  )
}

/**
 * アイテムの表示名を取得（保護メッセージ表示用）
 * @param {Object} item - アイテム
 * @returns {string} 表示名
 */
export function getItemDisplayName(item) {
  if (!item) return ''
  return item.name || item.memo || item.original_filename || ''
}

/**
 * 保護されたアイテムの削除を試みた時のメッセージを表示
 *
 * @param {Object} options - オプション設定
 * @param {string} options.itemName - アイテム名（表示用）
 * @param {Object} options.item - アイテムオブジェクト（itemNameが指定されていない場合に使用）
 */
export function showProtectedMessage(options = {}) {
  let displayName = options.itemName
  if (!displayName && options.item) {
    displayName = getItemDisplayName(options.item)
  }

  const message = displayName
    ? `「${displayName}」は保護されているため削除できません。<br><br>削除するには、名前やタグから保護キーワード（保護、ロック、プロテクト）を削除してください。`
    : 'このアイテムは保護されているため削除できません。<br><br>削除するには、名前やタグから保護キーワード（保護、ロック、プロテクト）を削除してください。'

  store.dispatch('messageDialog/open', {
    id: 'protected-item',
    title: '削除不可',
    contentHtml: message,
    options: {
      icon: 'mdi-lock',
      iconColor: 'warning',
      maxWidth: 400,
      closeText: '閉じる',
      closeColor: 'warning',
      showCloseIcon: true,
    },
  })
}
